import { Box, Typography, IconButton, useTheme, Fade, Backdrop } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useChatClient } from './useChatClient';
import { useNavigate } from 'react-router-dom';
import ExoAvatar from '../exo/ExoAvatar';
import CloseIcon from '@mui/icons-material/Close';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

export const ChatUrgentMessageOverlay = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { urgentMessage, dismissUrgentMessage } = useChatClient();

  // Auto-dismiss after 30 seconds
  useEffect(() => {
    if (urgentMessage) {
      const timer = setTimeout(() => {
        dismissUrgentMessage();
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [urgentMessage, dismissUrgentMessage]);

  const handleClick = () => {
    if (urgentMessage) {
      navigate(`/chat?conversation=${urgentMessage.conversationId}`);
      dismissUrgentMessage();
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  if (!urgentMessage) return null;

  const senderName = urgentMessage.sender?.name ||
    `${urgentMessage.sender?.firstName || ''} ${urgentMessage.sender?.lastName || ''}`.trim() ||
    'Unknown';

  return (
    <Backdrop
      open={Boolean(urgentMessage)}
      sx={{
        zIndex: 99999,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Fade in={Boolean(urgentMessage)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            maxWidth: '90vw',
            maxHeight: '90vh',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={dismissUrgentMessage}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              color: colors.text,
              backgroundColor: colors.glass,
              backdropFilter: 'blur(8px)',
              '&:hover': {
                backgroundColor: colors.card,
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Urgent badge */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 4,
              px: 3,
              py: 1,
              borderRadius: '20px',
              backgroundColor: colors.error,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 },
              },
            }}
          >
            <PriorityHighIcon sx={{ color: 'white' }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: 'white', textTransform: 'uppercase' }}>
              {t('Urgent Message')}
            </Typography>
          </Box>

          {/* Sender info */}
          <Box
            onClick={handleClick}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              p: 4,
              borderRadius: '24px',
              backgroundColor: colors.glass,
              backdropFilter: 'blur(12px)',
              border: `2px solid ${colors.error}`,
              boxShadow: `0 0 40px ${colors.error}40`,
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.02)',
              },
            }}
          >
            <ExoAvatar
              size="80px"
              name={senderName}
              picture={urgentMessage.sender?.picture?.temporaryUrl || urgentMessage.sender?.picture?.url || urgentMessage.sender?.avatar}
            />
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ color: colors.text, mt: 2, textAlign: 'center' }}
            >
              {senderName}
            </Typography>

            {/* Message content */}
            <Box
              sx={{
                mt: 3,
                p: 3,
                borderRadius: '16px',
                backgroundColor: colors.bgInput,
                maxWidth: 500,
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: colors.text,
                  textAlign: 'center',
                  lineHeight: 1.6,
                }}
              >
                {stripHtml(urgentMessage.content)}
              </Typography>
            </Box>

            {/* Click to open hint */}
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.6,
                mt: 3,
              }}
            >
              {t('Click to open conversation')}
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Backdrop>
  );
};
